import * as Sentry from '@sentry/browser';

interface SentryConfig {
  enabled: boolean;
  dsn: string;
  environment: string;
  debug: boolean;
  tracesSampleRate: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

// Get configuration from window object
const getSentryConfig = (): SentryConfig => {
  const globalConfig = (window as any)?.sentryConfig;

  return {
    enabled: globalConfig?.enabled || false,
    dsn: globalConfig?.dsn || '',
    environment: globalConfig?.environment || 'dev',
    debug: globalConfig?.debug || false,
    tracesSampleRate: globalConfig?.tracesSampleRate || 0.1,
    replaysSessionSampleRate: globalConfig?.tracesSampleRate || 0.1,
    replaysOnErrorSampleRate: 0.5,
  };
};

// Initialize Sentry
export const initSentry = (): void => {
  const config = getSentryConfig();

  // Don't initialize if Sentry is disabled
  if (!config.enabled) {
    console.log('Sentry is disabled via configuration');
    return;
  }

  // Don't initialize if no DSN is provided
  if (!config.dsn) {
    console.warn('Sentry DSN not provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    debug: config.debug,
    tracesSampleRate: config.tracesSampleRate,

    maxBreadcrumbs: 100,
    attachStacktrace: true,
    sendDefaultPii: false,

    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: (url: string) => {
          return url.startsWith('/') || url.startsWith(window.location.origin);
        },
      }),

      // Add breadcrumbs for user interactions
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        xhr: true,
        fetch: true,
        history: true,
      }),

      // Add global error handlers
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],

    // Set initial tags
    initialScope: (scope: Sentry.Scope) => {
      scope.setTag('component', 'frontend');
      scope.setTag('browser', navigator.userAgent);
      scope.setTag('url', window.location.href);
      return scope;
    },
  });
};

// Export Sentry for use in other modules
export { Sentry };

// Utility functions for manual error reporting
export const captureException = (error: Error, context?: Record<string, any>): void => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info'): void => {
  Sentry.captureMessage(message, level);
};

export const addBreadcrumb = (message: string, category = 'custom', data?: Record<string, any>): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

// Simple initialization - config should be available since script is in head
const initializeSentry = (): void => {
  // Try immediate initialization
  if ((window as any)?.sentryConfig) {
    initSentry();
    return;
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSentry(), { once: true });
  } else {
    initSentry();
  }
};

initializeSentry();
